/**
 * AI behaviour and the pricing it reasons about.
 *
 * There is no API key field here on purpose: the key lives in the server
 * environment and never reaches the browser.
 */
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import { priceOf, modulePrice } from '../../domain/pricing.js';
import { Section, Grid, Row, CheckGrid, Toggle, NumberField, TextField } from './SettingsBits.jsx';

const TONES = ['Friendly and direct', 'Warm and personal', 'Formal and businesslike', 'Short and blunt'];

export default function AIPane() {
  const { S, patchSettings, updateSettings, clearAIResults, toast } = useApp();

  const setPrice = (plan, value) => updateSettings(next => {
    next.prices = { ...(next.prices || {}), [plan]: Math.max(0, parseFloat(value) || 0) };
  });

  const setModulePrice = (name, value) => updateSettings(next => {
    next.modules = { ...(next.modules || {}), [name]: Math.max(0, parseFloat(value) || 0) };
  });

  const addModule = () => {
    // eslint-disable-next-line no-alert
    const name = (window.prompt('Module name:') || '').trim();
    if (!name) return;
    if (S.modules && name in S.modules) { toast('That module already exists'); return; }
    updateSettings(next => { next.modules = { ...(next.modules || {}), [name]: 0 }; });
    toast('Module added');
  };

  return (
    <>
      <Section
        title="AI features"
        hint="The copilot, daily brief, risk scoring, smart search and email drafting all read this workspace. The key stays on the server."
      >
        <CheckGrid>
          <label>
            <input
              type="checkbox"
              checked={S.aiOn !== false}
              onChange={e => patchSettings({ aiOn: e.target.checked })}
            />
            Enable AI features
          </label>
        </CheckGrid>
      </Section>

      <Section title="How emails should sound" hint="Used when drafting outreach.">
        <Grid>
          <TextField label="Sender name" name="senderName" placeholder="e.g. Agalya" />
          <TextField label="Company" name="companyName" />
          <div className="num">
            <label>Tone</label>
            <select
              value={S.tone || TONES[0]}
              onChange={e => patchSettings({ tone: e.target.value })}
            >
              {TONES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
        </Grid>
      </Section>

      <Section
        title="Plan pricing"
        hint="Drives MRR, revenue at risk, and what the AI knows your plans are worth."
      >
        <Grid>
          <TextField label="Currency" name="currency" />
          {(S.plans || []).map(plan => (
            <div className="num" key={plan}>
              <label>{plan} / month</label>
              <input
                type="number"
                min="0"
                step="1"
                value={priceOf(S, plan)}
                onChange={e => setPrice(String(plan).toUpperCase(), e.target.value)}
              />
            </div>
          ))}
          <NumberField label="Annual discount" name="annualDiscount" unit="percent" min={0} max={90} />
        </Grid>
      </Section>

      <Section
        title="Add-on modules"
        hint="Charged on top of the plan unless the plan bundles them. These start at zero — set what you actually charge."
      >
        <Grid>
          {Object.keys(S.modules || {}).map(name => (
            <div className="num" key={name}>
              <label>{name}</label>
              <input
                type="number"
                min="0"
                step="1"
                value={modulePrice(S, name)}
                onChange={e => setModulePrice(name, e.target.value)}
              />
              <small>per month</small>
            </div>
          ))}
        </Grid>
        <p style={{ marginTop: 9 }}>
          Bundled free on: <b>{(S.bundlePlans || []).join(', ') || 'no plans'}</b>
        </p>
        <Row>
          <button type="button" className="btn" onClick={addModule}><Icon name="plus" />Add a module</button>
        </Row>
      </Section>

      <Section title="Reset AI output" hint="Clears the brief, the analysis and every stored risk score.">
        <Row>
          <button type="button" className="btn danger" onClick={clearAIResults}>Clear AI results</button>
        </Row>
      </Section>
    </>
  );
}
